import { message } from "antd";
import config from "../../../config";
import { HttpRequest } from "../../../utils";
import { AssistantFunctionParams, WhatsAppConfigParams, SaveAppearanceParams } from "../types";
import { toColorString, transformAssistantData, transformAIResponseData } from "../utils";
import { NameDuplicateError } from "../errors";

import { createLogger } from '../../../utils/logger';
const logger = createLogger('features-assistant-management');

export const assistantService = {
  getAssistants: async (accountId: string) => {
    try {
      const request = new HttpRequest(`${config.endpoints.assistants}/account/${accountId}`);
      const response = await request.get();
      logger.info(' Response from getAssistants:', response);
      return response?.data?.chatbots || [];
    } catch (err) {
      logger.error('Error fetching assistants:', err);
      message.error('Error loading assistants');
      return [];
    }
  },

  getAssistantsCount: async (accountId: string) => {
    try {
      const request = new HttpRequest(`${config.endpoints.assistants}/account/${accountId}`);
      const response = await request.get();
      const chatbots = response?.data?.chatbots || [];
      return chatbots.length;
    } catch (err) {
      logger.error('Error fetching assistants count:', err);
      return 0;
    }
  },

  handleDuplicateAgent: async ({ id, loadAssistants, currentAccountId }: AssistantFunctionParams) => {
    try {
      const request = new HttpRequest(`${config.endpoints.chatbot}/duplicate`);
      await request.post({ chatbotId: id });
      message.success('Agent duplicated successfully');
      if (currentAccountId) {
        await loadAssistants(currentAccountId);
      } else {
        message.warning('Could not reload assistants list: missing account ID');
      }
    } catch (err) {
      logger.error('Error duplicating agent:', err);
      message.error('Error duplicating agent');
    }
  },
  
  handleDeleteAgent: async ({ id, loadAssistants, currentAccountId }: AssistantFunctionParams) => {
    try {
      const request = new HttpRequest(config.endpoints.chatbot);
      await request.delete(id);
      message.success('Agent deleted successfully');
      if (currentAccountId) {
        await loadAssistants(currentAccountId);
      } else {
        message.warning('Could not reload assistants list: missing account ID');
      }
    } catch (err) {
      logger.error('Error deleting agent:', err);
      message.error('Error deleting agent');
    }
  },
  
  handlePersonalitySave: async ({ values, mode, currentAssistant, currentAccount, onCreated, navigate }) => {
    try {
      const body = transformAssistantData(values, currentAssistant);
      logger.info('Transformed data to send:', body);
      const request = new HttpRequest(config.endpoints.chatbot);

      let assistantId = currentAssistant?.id;

      if (mode === 'edit') {
        logger.info('Sending PUT request with data:', body);
        await request.put(currentAssistant.id, body);

        // Save all business rules (both manual and AI-suggested)
        if (values.advancedConfig?.businessRules && values.advancedConfig.businessRules.length > 0) {
          const businessRulesToSave = values.advancedConfig.businessRules.map(rule => rule.rule);
          logger.info('Saving business rules', { count: businessRulesToSave.length });

          // Update all business rules at once
          await assistantService.updateBusinessRules(currentAssistant.id, businessRulesToSave);
          message.success('Changes saved successfully, including business rules');
        } else {
          message.success('Changes saved successfully');
        }
      } else {
        const accountIdToSend = currentAccount?.id || values.currentAccount?.id;
        if (!accountIdToSend) {
          message.error('Missing currentAccount?.id to create chatbot.');
          return;
        }

        logger.info('Sending request to create agent:', { ...body, accountId: accountIdToSend });
        const response = await request.post({ ...body, accountId: accountIdToSend });
        logger.info('Response from create agent:', response);

        if (response && response.status && response.status > 201) {
          const errorMsg = response.data?.message || 'Unexpected server response while creating agent';
          if (response.status === 409) {
            throw new NameDuplicateError(errorMsg);
          }
          throw new Error(errorMsg);
        }

        if (response?.data) {
          assistantId = response.data.id;

          // Save all business rules (both manual and AI-suggested) for new agent
          if (values.advancedConfig?.businessRules && values.advancedConfig.businessRules.length > 0) {
            const businessRulesToSave = values.advancedConfig.businessRules.map(rule => rule.rule);
            logger.info('Saving business rules for new agent', {
              assistantId,
              count: businessRulesToSave.length
            });

            // Add each business rule
            for (const rule of businessRulesToSave) {
              await assistantService.addBusinessRule(assistantId, rule);
            }
            message.success('Agent created successfully, including business rules');
          } else {
            message.success('Agent created successfully');
          }

          if (onCreated) onCreated(assistantId);
          setTimeout(() => {
            navigate(`/accounts/${accountIdToSend}/assistants`);
          }, 100);
        } else {
          throw new Error('No data received from server');
        }
      }
    } catch (error) {
      logger.error('Error in handlePersonalitySave:', error);
      message.error('Error saving changes');
      throw error;
    }
  },
  
  handleGenerateAI: async ({ aiPrompt, mode, currentAssistant, form }) => {
    try {
      if (!aiPrompt) {
        message.warning('Please enter a description first');
        return;
      }

      const response = await fetch(`${config.urls.api}/generator/GenerateAI`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          prompt: aiPrompt,
          ...(mode === 'edit' && currentAssistant?.id ? { chatbotId: currentAssistant.id } : {}),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        message.error(data.message || 'Failed to generate AI response');
        return;
      }

      if (data.response && data.response[0]) {
        try {
          const formValues = transformAIResponseData(data.response[0]);
          form.setFieldsValue(formValues);
          message.success('AI configuration applied successfully');
          return formValues;
        } catch (parseError) {
          logger.error('Error parsing AI response:', parseError);
          message.error('Error processing AI response format');
          throw parseError;
        }
      } else {
        const msg = 'AI generation was successful but the response format was unexpected. Please check your request.';
        message.warning(data.message || msg);
        throw new Error(msg);
      }
    } catch (error) {
      logger.error('Error generating AI response:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate AI response due to an unexpected error.';
      message.error(errorMessage);
      throw error;
    }
  },
  
  updateWhatsAppConfig: async ({ chatbotId, whatsappConfig }: WhatsAppConfigParams) => {
    try {
      const request = new HttpRequest(config.endpoints.chatbot);
      await request.put(chatbotId, { whatsappConfig });
      message.success('WhatsApp settings updated successfully');
    } catch (error) {
      logger.error('Error updating WhatsApp config:', error);
      message.error('Failed to update WhatsApp settings');
      throw error;
    }
  },

  handleSave: async ({ appearanceState, currentAssistant }: SaveAppearanceParams) => {
    try {
      const request = new HttpRequest(config.endpoints.chatbot);
      logger.info('Current assistant before update:', currentAssistant);
      
      logger.info('Received appearance state:', appearanceState);
      
      const updatedConfig = {
        ...currentAssistant,
        greeting: appearanceState.greetingMessage,
        webchatConfig: {
          ...currentAssistant?.webchatConfig,
          colorMain: toColorString(appearanceState.headerColor),
          colorTitle: toColorString(appearanceState.primaryColor),
          iconBgColor: toColorString(appearanceState.iconColor),
          colorBg: toColorString(appearanceState.backgroundColor),
          chatbotBgMessageColor: toColorString(appearanceState.backgroundMessageColor),
          userBgMessageColor: toColorString(appearanceState.backgroundMessageColor),
          chatbotTextMessageColor: toColorString(appearanceState.textColor),
          userTextMessageColor: toColorString(appearanceState.textColor),
          greeting: appearanceState.greetingMessage,
          botName: currentAssistant?.name,
          voiceLanguage: currentAssistant?.voiceLanguage || currentAssistant?.webchatConfig?.voiceLanguage || 'off',
        },
      };
      
      logger.info('Sending webchat config:', updatedConfig.webchatConfig);

      logger.info('Sending update with config:', updatedConfig);
      const response = await request.put(currentAssistant?.id, updatedConfig);
      logger.info('Update response:', response);
      logger.info('Response status:', response?.status);
      logger.info('Response data structure:', response?.data);

      // Check if the request was successful (2xx status codes)
      if (response?.status >= 200 && response?.status < 300) {
        // If successful, return the response regardless of data structure
        // The component will handle the data appropriately
        return response;
      } else {
        // If not successful, throw error with more details
        const errorMessage = response?.data?.message || response?.data?.error || 'Update failed';
        throw new Error(`Server responded with status ${response?.status}: ${errorMessage}`);
      }
    } catch (error) {
      logger.error('Error saving appearance:', error);
      throw error;
    }
  },

  // Business Rules API methods
  getBusinessRules: async (chatbotId: string) => {
    try {
      const request = new HttpRequest(`${config.urls.api}/chatbot/${chatbotId}/business-rules`);
      logger.info('Requesting business rules for chatbot:', chatbotId);
      logger.info('Request:', request);
      const response = await request.get();
      logger.info('Response:', response);
      // Debug logs
      logger.info('Full response from API:', response);
      logger.info('Response data:', response?.data);
      logger.info('Response data.data:', response?.data?.data);
      logger.info('Business rules:', response?.data?.data?.businessRules);
      
      // The business rules are nested in response.data.data.businessRules
      return response?.data?.data || { businessRules: [], count: 0 };
    } catch (error) {
      logger.error('Error fetching business rules:', error);
      message.error('Error loading business rules');
      return { businessRules: [], count: 0 };
    }
  },

  addBusinessRule: async (chatbotId: string, rule: string) => {
    try {
      const request = new HttpRequest(`${config.urls.api}/chatbot/${chatbotId}/business-rules`);
      const response = await request.post({ rule });
      return response?.data?.data || {};
    } catch (error) {
      logger.error('Error adding business rule:', error);
      message.error('Error adding business rule');
      throw error;
    }
  },

  updateBusinessRules: async (chatbotId: string, businessRules: string[]) => {
    try {
      const request = new HttpRequest(`${config.urls.api}/chatbot/${chatbotId}/business-rules`);
      const response = await request.put('', { businessRules });
      message.success('Business rules updated successfully');
      return response?.data?.data || {};
    } catch (error) {
      logger.error('Error updating business rules:', error);
      message.error('Error updating business rules');
      throw error;
    }
  },

  deleteBusinessRule: async (chatbotId: string, rule: string) => {
    try {
      const request = new HttpRequest(`${config.urls.api}/chatbot/${chatbotId}/business-rules`);
      const response = await request.delete('', { rule });
      message.success('Business rule deleted successfully');
      return response?.data?.data || {};
    } catch (error) {
      logger.error('Error deleting business rule:', error);
      message.error('Error deleting business rule');
      throw error;
    }
  }
};